import { useEffect, useState } from "react";
import { AlertTriangle, Info, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg } from "../ui";
import { PLATFORM_LABEL, TONE, fmt, fmtDate } from "./meta";

const LABEL = { add: "Add", move: "Move", remove: "Remove", create_subscription: "New subs" };

function Note({ tone, icon: Icon, children, testid }) {
  return <div className={`flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${tone}`} data-testid={testid}><Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" /><div>{children}</div></div>;
}

export function ApplyDialog({ run, sel, onClose, onStarted }) {
  const [body] = useState(() => ({ seqs: [...sel.seqs], domains: [...sel.domains] }));
  const [plan, setPlan] = useState(null);
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.post(`/billing-sync/preview/runs/${run.id}/apply/plan`, body).then(({ data }) => setPlan(data))
      .catch((e) => { toast.error(errMsg(e, "Could not check the selection")); onClose(); });
  }, [run.id, body, onClose]);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/billing-sync/preview/runs/${run.id}/apply`, body);
      toast.success(`Applying ${fmt(data.total)} change${data.total === 1 ? "" : "s"} to Stax Bill`);
      onStarted(data.id);
    } catch (e) { toast.error(errMsg(e, "Could not start the apply")); setBusy(false); }
  };
  const removes = plan?.by_kind?.remove || 0;
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-lg" data-testid="bsp-apply-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Apply to Stax Bill</DialogTitle>
          <DialogDescription>These changes are made in Stax Bill straight away, the same way the tracked-items scripts make them.</DialogDescription>
        </DialogHeader>
        {!plan ? <Loader2 className="mx-auto my-8 h-5 w-5 animate-spin text-slate-300" /> : (
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-2">{["add", "move", "remove", "create_subscription"].map((k) => (
              <div key={k} className="rounded-md border border-slate-200 px-3 py-2" data-testid={`bsp-plan-${k}`}>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{LABEL[k]}</div>
                <div className={`font-heading text-xl font-bold tabular-nums ${plan.by_kind[k] ? TONE[k].text : "text-slate-300"}`}>{fmt(plan.by_kind[k])}</div>
              </div>))}</div>
            <p className="text-xs text-slate-500" data-testid="bsp-plan-scope">{fmt(plan.domains)} domain{plan.domains === 1 ? "" : "s"} · {Object.entries(plan.by_platform).map(([p, n]) => `${PLATFORM_LABEL[p]} ${fmt(n)}`).join(" · ") || "nothing"}</p>
            {removes > 0 && !plan.is_month_end && (
              <Note tone="border-rose-200 bg-rose-50 text-rose-900" icon={AlertTriangle} testid="bsp-plan-month-end-warning">
                Removals normally wait for the month-end run on <b>{fmtDate(plan.month_end)}</b>. Removing {fmt(removes)} item{removes === 1 ? "" : "s"} now stops billing for {removes === 1 ? "it" : "them"} straight away.</Note>)}
            {plan.by_kind.create_subscription > 0 && (
              <Note tone="border-violet-200 bg-violet-50 text-violet-900" icon={Info} testid="bsp-plan-new-subs">
                {fmt(plan.by_kind.create_subscription)} new subscription{plan.by_kind.create_subscription === 1 ? " is" : "s are"} created and activated straight away (invoice day 1), and {fmt(plan.planned_items)} extension{plan.planned_items === 1 ? "" : "s"} and number{plan.planned_items === 1 ? "" : "s"} are added to {plan.by_kind.create_subscription === 1 ? "it" : "them"} in the same apply.</Note>)}
            {plan.run_age_hours > 24 && (
              <Note tone="border-amber-200 bg-amber-50 text-amber-900" icon={AlertTriangle} testid="bsp-plan-age-warning">
                This preview is {Math.round(plan.run_age_hours)} hours old. If the platform changed since, run a fresh preview first.</Note>)}
            <Note tone="border-slate-200 bg-slate-50 text-slate-600" icon={Info}>
              Each item is checked live in Stax Bill right before it changes. Items that changed since the preview are skipped, and a move that can't be added to its new place is put back where it was.</Note>
            {plan.total === 0 ? <p className="text-sm text-slate-600" data-testid="bsp-plan-empty">Nothing left to apply — these rows were already applied or skipped.</p> : (
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <Checkbox checked={ok} onCheckedChange={(v) => setOk(!!v)} data-testid="bsp-apply-confirm-check" />
                I understand this changes billing in Stax Bill</label>)}
          </div>)}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy} data-testid="bsp-apply-cancel">Cancel</Button>
          <Button onClick={go} disabled={!plan || !plan.total || !ok || busy} className="bg-slate-900" data-testid="bsp-apply-confirm">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Apply {fmt(plan?.total)} change{plan?.total === 1 ? "" : "s"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
